import {
  Component,
  HostBinding,
  Input,
  OnInit
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { OnyxEvent } from '../../models/event';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { Payment } from '../../models/payment';
import { PaymentModifier } from '../../models/payment-modifier';
import { PaymentStore } from '../../service/payment.store';
import { LogService } from '../../../shared/log/log.service';
import { ReservationStore } from '../../service/reservation-store';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { AdjustmentStore } from '../../service/adjustment.store';
import { RsvpStore } from '../../service/rsvp.store';
import { PaymentStatus } from '../../models/payment-status.enum';
import { Adjustment } from '../../models/adjustment';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { map } from 'rxjs/operators';
import { NumberUtils } from '../../../shared/utils/number.util';

@Component({
  selector: 'pioneer-payment-modifier-display',
  templateUrl: './payment-modifier-display.component.html',
  styleUrls: [ './payment-modifier-display.component.css' ]
})
export class PaymentModifierDisplayComponent implements OnInit {

  @HostBinding('class') defaultClasses = 'col form-container due-amount';
  @HostBinding('class.has-adjust') hasModifiersCssClass = false;

  @Input()
  @HostBinding('class.status-rejected')
  strikeThroughPaymentLines = false;

  @Input()
  strikeThroughFinalPayment = false;

  @Input()
  strikeThroughPrepayment = false;

  public onyxEvent: OnyxEvent;

  prepayment: Payment;

  displayModifiers = false;

  constructor(
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private logService: LogService,
    private paymentStore: PaymentStore,
    private reservationStore: ReservationStore,
    private rsvpStore: RsvpStore,
    private adjustmentStore: AdjustmentStore
  ) {}

  @Input()
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit() {
    this.getPrepayment().subscribe(prepayment => {
      this.prepayment = prepayment;
      if (prepayment !== null) {
        this.hasModifiersCssClass = true;
      }
    });

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in AddPaymentComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'PaymentEquationComponent'
        })
      );
    }

    this.latestPayment.map(payment => {
      this.displayModifiers = payment && PaymentStatus.REQUESTED !== payment.status && PaymentStatus.RESENT_REQUEST !== payment.status;
      if (this.displayModifiers) {
        this.areThereAnyModifiers().subscribe( modifierExists => {
          if (modifierExists) {
            this.hasModifiersCssClass = true;
          }
        });
      } else {
        this.modifiers.map(modifiers => this.hasModifiersCssClass = modifiers && modifiers.length > 0).take(1).subscribe();
      }
    }).take(1)
    .subscribe();
  }

  get latestPayment(): Observable<Payment> {
    return this.paymentStore.getLatestFinalPayment();
  }

  get calculatedPayment(): Observable<number> {
    return this.adjustmentStore.getCalculatedPayment(ReservationStatusCode.CHECKED_OUT, this.onyxEvent.eventProperty.subscriber)
      .map((adjustments) => {
        return (adjustments ? adjustments : 0) - (this.prepayment ? this.prepayment.commissionAmount : 0);
    });
  }

  get modifiers(): Observable<PaymentModifier[]> {
    return this.latestPayment.share().map(payment => {
      let result: PaymentModifier[];
      this.logService.debug('modifiers: payment' + payment);
      if (!payment) {
        result = [];
      } else {
        this.logService.debug('modifiers: eventPaymentModifiers: ' + payment.eventPaymentModifiers);
        result = payment.eventPaymentModifiers;
      }
      return result;
    });
  }

  get adjustments(): Observable<Adjustment[]> {
    return this.adjustmentStore.adjustmentsAsArray;
  }

  get totalModifiersAmount(): Observable<number> {
    return this.modifiers.map(modifierArray => {
      return modifierArray.map(modifier => modifier.adjustmentAmount)
        .filter(amt => amt !== null)
        .reduce((prev: number, current: number) => !!current ? prev + current : prev, 0);
    });
  }

  get totalCommissions(): Observable<number> {
    if (this.onyxEvent.eventProperty.subscriber) {
      return this.reservationStore.getTotalCommissions(ReservationStatusCode.CHECKED_OUT);
    } else {
      return this.rsvpStore.totalCommission;
    }
  }

  get totalRevenue(): Observable<number> {
    if (this.onyxEvent.eventProperty.subscriber) {
      return this.reservationStore.getTotalRevenue(ReservationStatusCode.CHECKED_OUT);
    } else {
      return this.rsvpStore.totalRevenue;
    }
  }

  private areThereAnyModifiers(): Observable<boolean> {
    return this.modifiers.map(modifierArray => {

      let hasModifiers = false;

      if (modifierArray && modifierArray.length > 0) {
        hasModifiers = true;
      }

      this.logService.debug('areThereAnyModifiers: modifierArray: ' + modifierArray);
      this.logService.debug('areThereAnyModifiers: hasModifiers: ' + hasModifiers);

      return hasModifiers;
    });
  }

  getModifierCssClass(adjustment: PaymentModifier | Adjustment): string {
    if (adjustment.adjustmentAmount >= 0) {
      return 'adjustment up';
    } else {
      return 'adjustment down';
    }
  }

  getModifierAmountCssClass(adjustment: PaymentModifier | Adjustment): string {
    if (adjustment.adjustmentAmount >= 0) {
      return 'currency text-success';
    } else {
      return 'currency text-warning';
    }
  }

  private getPrepayment(): Observable<Payment> {
    return this.paymentStore.getPrepayment()
      .map((prepayment) => {
        return (prepayment && prepayment.status) ? prepayment : null;
      }).share();
  }

  get calculatedTotal(): Observable<number> {
    return this.latestPayment.map(payment => {
      const finalPaymentAmount = payment && payment.commissionAmount ? payment.commissionAmount : 0;
      const prepaymentAmount = (this.prepayment && this.prepayment.commissionAmount) ? this.prepayment.commissionAmount : 0;
      return finalPaymentAmount - prepaymentAmount;
    });
  }

  getAmount(amount) {
    return amount ? amount : 0;
  }

  get commissionPercentage(): number {
    return this.onyxEvent.commissionRoomPercentage ? this.onyxEvent.commissionRoomPercentage / 100 : 0;
  }

  isReducedCommission(totalRevenue, totalCommission) {
    return NumberUtils.round2Fixed(totalCommission) < NumberUtils.round2Fixed(totalRevenue * this.commissionPercentage);
  }

  getReducedCommissionTooltip() {
    return this.onyxEvent.eventProperty.subscriber ? 'EVENT.COMMISSION.TOOLTIP_REDUCED_COMMISSION_RESERVATIONS'
      : 'EVENT.COMMISSION.TOOLTIP_REDUCED_COMMISSION_RSVPS';
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/payment/payment-modifier-display-calculation/payment-modifier-display.component.ts